export class CacheKV implements AsyncIterable<[string, ArrayBuffer], void, void> {
    #c: Cache;
    #n: string;
    constructor(name: string, cache: Cache) {
        this.#c = cache;
        this.#n = name;
    }

    static open = async (name: string) => {
        // Fix for Safari/iOS: globalThis/self.caches might be undefined or inaccessible in some contexts
        if (typeof self === 'undefined' || !self.caches) {
            console.warn("Cache API not supported or accessible. Falling back to in-memory/no-op.");
            // Return a dummy implementation to avoid crashes
            return new CacheKV(name, null as any);
        }
        try {
            const c = await self.caches.open(name);
            return new CacheKV(name, c);
        } catch (e) {
            console.warn("Failed to open cache:", e);
            return new CacheKV(name, null as any);
        }
    };

    static #keyToURL = (key: string) => {
        return new URL(`https://httpbin.org/anything/${encodeURIComponent(key)}`);
    };

    static #urlToKey = (url: string) => {
        return decodeURIComponent(url.substring("https://httpbin.org/anything/".length));
    };

    has = async (key: string) => {
        if (!this.#c) return false;
        try {
            const resp = await this.#c.match(CacheKV.#keyToURL(key));
            return resp ? true : false;
        } catch (e) {
            console.warn("Cache match failed:", e);
            return false;
        }
    };

    set = async (key: string, value: Blob | BufferSource) => {
        if (!this.#c) return;
        try {
            const resp = new Response(value, { status: 200 });
            await this.#c.put(CacheKV.#keyToURL(key), resp);
        } catch (e) {
            console.warn("Cache put failed:", e);
        }
    };

    get = async (key: string) => {
        if (!this.#c) return undefined;
        try {
            const resp = await this.#c.match(CacheKV.#keyToURL(key));
            if (resp) {
                return await resp.arrayBuffer();
            }
        } catch (e) {
            console.warn("Cache get failed:", e);
        }
        return undefined;
    };

    keys = async () => {
        if (!this.#c) return [];
        try {
            const keys = await this.#c.keys();
            return keys.map((req) => CacheKV.#urlToKey(req.url));
        } catch (e) {
            console.warn("Cache keys failed:", e);
            return [];
        }
    };

    setString = async (key: string, value: string) => {
        const data = new TextEncoder().encode(value);
        await this.set(key, data);
    };

    setJsonObject = async (key: string, value: unknown) => {
        const data = JSON.stringify(value);
        await this.setString(key, data);
    };

    getString = async (key: string, defaultValue: string = "") => {
        const data = await this.get(key);
        if (data) {
            return new TextDecoder().decode(data);
        }
        return defaultValue;
    };

    getJsonObject = async <T>(key: string, defaultValue: T | undefined = undefined) => {
        const data = await this.getString(key, "");
        if (data !== "") {
            return JSON.parse(data) as T;
        }
        return defaultValue;
    };

    clear = async () => {
        if (!this.#c) return;
        try {
            if (typeof self !== 'undefined' && self.caches) {
                await self.caches.delete(this.#n);
                this.#c = await self.caches.open(this.#n);
            }
        } catch (e) {
            console.warn("Cache clear failed:", e);
        }
    };

    static #asyncGenerator = async function* (kv: CacheKV) {
        const keys = await kv.keys();
        for (const k of keys) {
            yield [k, await kv.get(k)] as [string, ArrayBuffer];
        }
    };
    [Symbol.asyncIterator] = () => {
        return CacheKV.#asyncGenerator(this);
    };
}
